import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";

// Utils
import {describeRestriction} from "./helpers";

// Components
import Accordion from "../../components/Accordion";
import Help from "../../components/Help";
import VisibilityConflictWarning from "./VisibilityConflictWarning";

// Style
import {z} from "../../style/components/mixins";
import {voice} from "../../style/components/typography";
import {border, pad, radius} from "../../style/components/variables";
import {Abbr, List, ListItem, Pill, Text} from "../../style/components/general";

const ChecksheetRestrictions = ({showModalEdit, restrictions, state, conflicts = []}) => {
  const filteredDefault = restrictions.filter(restriction => {
    const {isState} = restriction;

    return !isState;
  });

  const filteredState = restrictions.filter(restriction => {
    const {isState} = restriction;

    return isState;
  });

  const renderRestrictions = lst =>
    lst.map(restriction => {
      const {label} = restriction;

      return (
        <Restriction key={label}>
          <StyledText bold>{label}</StyledText>
          <RestrictionDetails restriction={restriction} />
        </Restriction>
      );
    });

  return (
    <Menu>
      <Edit
        type="button"
        onClick={e => {
          e.stopPropagation();
          showModalEdit(true);
        }}>
        <Abbr title="Edit">
          <FontAwesomeIcon icon={faEdit} />
        </Abbr>
      </Edit>
      <Accordion
        arrowRight
        wrap
        compact
        testId="checksheet.restrictionsAccordion"
        label={
          <>
            Checksheet Restrictions&nbsp;
            <Help title="Checksheet restrictions">
              A restriction limits a set of fields to only the listed users or roles &ndash;
              everyone else with access to the checksheet will not see those fields.
            </Help>
          </>
        }>
        <RestrictionList>
          {restrictions?.length > 0 ? (
            <Wrapper>
              {(!state || filteredDefault?.length > 0) && (
                <>
                  {state && (
                    <Pill quiet basic>
                      DEFAULT
                    </Pill>
                  )}
                  <List column indent>
                    {renderRestrictions(filteredDefault)}
                  </List>
                  <br />
                </>
              )}
              {state && filteredState?.length > 0 && (
                <>
                  <Pill color={state.color ? `#${state.color}` : undefined} quiet basic>
                    {state.name}
                  </Pill>
                  <List column indent>
                    {renderRestrictions(filteredState)}
                  </List>
                </>
              )}
            </Wrapper>
          ) : (
            <Wrapper>
              <Text quiet>No restrictions created. Checksheet fields are visible to everyone.</Text>
            </Wrapper>
          )}
        </RestrictionList>
      </Accordion>
      <VisibilityConflictWarning conflicts={conflicts} />
    </Menu>
  );
};

ChecksheetRestrictions.propTypes = {
  showModalEdit: PropTypes.func.isRequired,
  restrictions: PropTypes.arrayOf(PropTypes.any).isRequired,
  state: PropTypes.objectOf(PropTypes.any),
  conflicts: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string))
};

// Style Overrides
const Menu = styled.div`
  position: relative;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  width: 100%;
  padding: ${pad}px;
  margin-bottom: ${pad}px;

  fieldset {
    padding-right: ${pad * 2}px;
  }

  ${Text} {
    max-width: 95%;
  }
`;

const Wrapper = styled.div`
  margin-top: ${pad}px;
`;

const Edit = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: ${z("above")};
  padding: ${pad}px;
  width: min-content;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const RestrictionList = styled.div`
  margin-bottom: ${pad}px;
`;

const Restriction = styled(ListItem)`
  margin-top: ${pad / 2}px;
  width: 100%;
`;

const StyledText = styled(Text)`
  display: flex;
  ${voice.small};
`;

const previewList = items => {
  const limit = 4;
  if (items.length <= limit) return items.join(", ");
  return `${items.slice(0, limit).join(", ")} +${items.length - limit} more`;
};

export const RestrictionDetails = ({restriction}) => {
  const {memberNames, fieldLabels, memberKind} = describeRestriction(restriction);
  if (!memberNames.length && !fieldLabels.length) return null;

  return (
    <Details>
      {memberNames.length > 0 && (
        <Text quiet title={memberNames.join(", ")}>
          Only {memberKind}: {previewList(memberNames)}
        </Text>
      )}
      {fieldLabels.length > 0 && (
        <Text quiet title={fieldLabels.join(", ")}>
          Can access fields: {previewList(fieldLabels)}
        </Text>
      )}
    </Details>
  );
};

RestrictionDetails.propTypes = {
  restriction: PropTypes.objectOf(PropTypes.any).isRequired
};

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pad / 4}px;
  margin-top: ${pad / 4}px;
`;

export default ChecksheetRestrictions;
